import React, { useState } from 'react';
import FloatingPopover from '../FloatingPopover';
import type { IMethod } from '../../stores/model/metamodel/method';

export interface MethodPopoverProps {
  /** The method to describe. */
  method: IMethod;
  /** Should the owner object be shown (default false). */
  showObject?: boolean;
  /** Should the owner event be shown (default true). */
  showEvent?: boolean;
  /** Is the popover enabled? (default true). */
  enabled?: boolean;
  /**
   * Preferred placement. See
   * https://github.com/littlebits/react-popover#code-preferplace-enum-string-null-code
   */
  preferPlace?: string | null;
  children?: React.ReactNode;
}

/**
 * A popover with information about a method.
 *
 * Uses FloatingPopover (a @floating-ui/react drop-in for the retired
 * react-popover, which crashed under React 19 via findDOMNode).
 *
 * Shown when hovering over a method in the OET or in the Inspector. A wrapper
 * div is added around the children. The popover is shown onMouseOver and hidden
 * onMouseOut.
 *
 * Props:
 * - method: the method
 * - showObject: should the owner object be shown (default false)
 * - showEvent: should the owner event be shown (default true)
 * - enabled: is the popover enabled? (default true)
 * - preferPlace: see
 *   https://github.com/littlebits/react-popover#code-preferplace-enum-string-null-code
 *
 * Not wrapped in observer(): the class wasn't either. It re-renders via its
 * observer parent (AttributesInspector / ObjectInspector / OET), which already
 * tracks the MST `method` it reads in renderBody. Wrapping it in observer() now
 * would change tracking semantics, so we preserve the plain-component behavior.
 */
export default function MethodPopover({
  method,
  showObject = false,
  showEvent = true,
  enabled = true,
  preferPlace,
  children,
}: MethodPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Mirrors the class `toggle(toState = null)`: when toState is null, flip from
  // the previous value; otherwise set it directly.
  const toggle = (toState: boolean | null = null) => {
    setIsOpen((prev) => (toState === null ? !prev : toState));
  };

  const renderBody = () => {
    return (
      <div className="method-popover-content">
        <div>
          <span>Name: </span>
          {method.name}
        </div>
        {showEvent && (
          <div>
            <span>In event: </span>
            {method.ownerEventType.name}
          </div>
        )}
        {showObject && (
          <div>
            <span>In object: </span>
            {method.ownerObjectType.name}
          </div>
        )}
        {method.viaDependency ? (
          <div>
            <span>Via dependency: </span>
            {method.viaDependency.displayName}
          </div>
        ) : null}
        {method.viaInheritance ? (
          <div>
            <span>Via inheritance: </span>
            {method.viaInheritance.utf8DisplayString}
          </div>
        ) : null}
        {method.viaMethod ? (
          <div>
            <span>Via method: </span>
            {method.viaMethod.asString()}
            {method.viaMethod.secondary ? ' (secondary)' : ''}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <FloatingPopover preferPlace={preferPlace} isOpen={enabled && isOpen} body={renderBody()}>
      <div
        className="method-popover-trigger"
        onMouseOver={() => toggle(true)}
        onMouseOut={() => toggle(false)}
        children={children}
      />
    </FloatingPopover>
  );
}
